import React, { useEffect, useMemo, useRef } from "react";
import styled from "styled-components";

const ScrollWrap = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  overflow: hidden;
`;

const ScrollTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px calc(50% - 34px);
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-x;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FadeLeft = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 52px;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(to right, #ffffff 20%, rgba(255, 255, 255, 0));
`;

const FadeRight = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 52px;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(to left, #ffffff 20%, rgba(255, 255, 255, 0));
`;

const TimePill = styled.button`
  flex: 0 0 auto;
  min-width: 68px;
  border: none;
  background: transparent;
  color: ${({ active }) => (active ? "#EE754C" : "#8a7d76")};
  font-size: ${({ active }) => (active ? "1rem" : "0.82rem")};
  font-weight: ${({ active }) => (active ? 950 : 800)};
  cursor: pointer;
  padding: 6px 8px;
  transform: ${({ active }) => (active ? "scale(1.08)" : "scale(1)")};
  transition:
    color 0.15s ease,
    transform 0.15s ease,
    font-size 0.15s ease;

  &:hover {
    color: #ee754c;
  }
`;

function normalizeTime(time) {
  if (!time) return "";
  return String(time).slice(0, 5);
}

function timeToMinutes(time) {
  const cleanTime = normalizeTime(time);
  const [hours, minutes] = cleanTime.split(":").map(Number);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  return hours * 60 + minutes;
}

function getRestaurantCurrentMinutes(timezone) {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone || "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());

    const hour = Number(parts.find((part) => part.type === "hour")?.value);
    const minute = Number(parts.find((part) => part.type === "minute")?.value);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      throw new Error("Invalid timezone time parts.");
    }

    return hour * 60 + minute;
  } catch (error) {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }
}

function getClosestTimeToNow(times, timezone) {
  if (!times.length) return null;

  const nowMinutes = getRestaurantCurrentMinutes(timezone);

  let bestTime = times[0];
  let bestDistance = Infinity;

  times.forEach((time) => {
    const minutes = timeToMinutes(time);

    if (minutes === null) return;

    const distance = Math.abs(minutes - nowMinutes);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestTime = time;
    }
  });

  return bestTime;
}

export default function ReservationTimeCenterScroll({
  times = [],
  value = null,
  onChange,
  restaurantTimezone = "Europe/London",
}) {
  const trackRef = useRef(null);
  const itemRefs = useRef({});
  const scrollFrameRef = useRef(null);
  const ignoreNextValueCenterRef = useRef(false);
  const didAutoCenterKeyRef = useRef("");

  const uniqueTimes = useMemo(() => {
    return Array.from(
      new Set((times || []).map(normalizeTime).filter(Boolean)),
    ).sort((a, b) => {
      return (timeToMinutes(a) || 0) - (timeToMinutes(b) || 0);
    });
  }, [times]);

  const uniqueTimesKey = uniqueTimes.join("|");

  const centerTime = (time, behavior = "smooth") => {
    const track = trackRef.current;
    const item = itemRefs.current[time];

    if (!track || !item) return;

    const itemCenter = item.offsetLeft + item.offsetWidth / 2;
    const targetLeft = itemCenter - track.clientWidth / 2;

    track.scrollTo({
      left: targetLeft,
      behavior,
    });
  };

  const selectClosestToCenter = () => {
    const track = trackRef.current;

    if (!track || !uniqueTimes.length) return;

    const center = track.scrollLeft + track.clientWidth / 2;

    let closestTime = uniqueTimes[0];
    let closestDistance = Infinity;

    uniqueTimes.forEach((time) => {
      const item = itemRefs.current[time];

      if (!item) return;

      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(itemCenter - center);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestTime = time;
      }
    });

    if (closestTime && closestTime !== value) {
      ignoreNextValueCenterRef.current = true;
      onChange?.(closestTime);
    }
  };

  const handleScroll = () => {
    if (scrollFrameRef.current) {
      cancelAnimationFrame(scrollFrameRef.current);
    }

    scrollFrameRef.current = requestAnimationFrame(selectClosestToCenter);
  };

  const handleTimeClick = (time) => {
    onChange?.(time);
    centerTime(time, "smooth");
  };

  useEffect(() => {
    if (!uniqueTimes.length) return;

    const autoCenterKey = `${uniqueTimesKey}|${restaurantTimezone}`;

    if (didAutoCenterKeyRef.current === autoCenterKey) return;

    const initialTime =
      value && uniqueTimes.includes(value)
        ? value
        : getClosestTimeToNow(uniqueTimes, restaurantTimezone);

    if (!initialTime) return;

    didAutoCenterKeyRef.current = autoCenterKey;

    onChange?.(initialTime);

    requestAnimationFrame(() => {
      centerTime(initialTime, "auto");
    });
  }, [uniqueTimesKey, restaurantTimezone]);

  useEffect(() => {
    if (!value || !uniqueTimes.includes(value)) return;

    if (ignoreNextValueCenterRef.current) {
      ignoreNextValueCenterRef.current = false;
      return;
    }

    centerTime(value, "smooth");
  }, [value, uniqueTimesKey]);

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  if (!uniqueTimes.length) return null;

  return (
    <ScrollWrap>
      <FadeLeft />

      <ScrollTrack ref={trackRef} onScroll={handleScroll}>
        {uniqueTimes.map((time) => (
          <TimePill
            key={time}
            ref={(element) => {
              if (element) {
                itemRefs.current[time] = element;
              }
            }}
            type="button"
            active={String(value) === String(time)}
            onClick={() => handleTimeClick(time)}
          >
            {time}
          </TimePill>
        ))}
      </ScrollTrack>

      <FadeRight />
    </ScrollWrap>
  );
}
